import { isFrankencoin } from '@1delta/lender-registry'
import type { TermAdapter } from './index'

const ppmToPercent = (v: unknown): number | undefined => {
  if (v == null) return undefined
  const n = Number(v)
  return Number.isFinite(n) ? n / 10_000 : undefined
}

/**
 * Frankencoin (ZCHF) — the generic sheet described this market wrongly on
 * three counts, each of which a borrower would act on.
 *
 *  1. **There is NO ORACLE.** The liquidation price is DECLARED by the
 *     position owner and policed by a Dutch-auction challenge game. So
 *     `challenge()` has no health gate at all: the trigger is
 *     `marketPrice < declaredPrice`, INDEPENDENT of how much is borrowed.
 *     Repaying does not reduce challenge risk — only re-declaring the price
 *     does. Our health factor here is a BORROW-CAPACITY metric, and the
 *     rule "HF < 1 ⇒ liquidatable" must never be applied to it.
 *
 *  2. **Debt ≠ proceeds.** Minting withholds a 10–40 % `reserveContribution`
 *     into the equity reserve and charges an upfront pro-rata fee, so you
 *     receive materially less than you owe. "Repay any time at no extra
 *     cost" — what the generic sheet said — is simply false.
 *
 *  3. **Positions EXPIRE.** There is a fixed `expiration`, so this is a
 *     fixed-term seam, not a perpetual CDP.
 */
export const frankencoinAdapter: TermAdapter = {
  id: 'frankencoin',
  matches: isFrankencoin,
  profileId: () => 'frankencoin.challenge@v1',
  build: (input) => {
    const fc = (input.market?.frankencoin ?? {}) as Record<string, unknown>
    const reservePct = ppmToPercent(fc.reserveContributionPPM)
    const upfrontPct = ppmToPercent(fc.upfrontFeePPM)
    const challengerPct = ppmToPercent(fc.challengerRewardPPM)
    const expiration = Number(fc.expiration ?? 0)
    const challengePeriod = Number(fc.challengePeriod ?? 0)

    const fees = [
      reservePct != null && reservePct > 0
        ? {
            id: 'reserve-contribution',
            label: 'Equity-reserve contribution',
            when: 'entry' as const,
            unit: 'percent' as const,
            basis: 'principal' as const,
            value: reservePct,
            payee: 'protocol' as const,
            description:
              'Withheld at mint into the FPS equity reserve. You owe the full amount but receive this much less, so the debt is NOT what you got.',
          }
        : null,
      upfrontPct != null && upfrontPct > 0
        ? {
            id: 'origination',
            label: 'Upfront interest',
            when: 'entry' as const,
            unit: 'percent' as const,
            basis: 'principal' as const,
            value: upfrontPct,
            payee: 'protocol' as const,
            description:
              'Charged once at mint, pro-rated over the time to expiry.',
          }
        : null,
    ].filter(Boolean) as NonNullable<unknown>[]

    return {
      // Not a coverage hole: the protocol has no price feed by design.
      oracle: { kind: 'none' },
      supply: {
        // Collateral inside a position. There is no ZCHF lending deposit at
        // all — the earn surface is the savings module, a separate provider —
        // so this side never pays interest.
        role: 'collateral',
        availability: { opensWith: 'both-legs' },
        counterparty: { kind: 'cdp', solvency: 'overcollateralized' },
      },
      borrow: {
        availability: { opensWith: 'both-legs' },
        debtShape: 'accruing',
        maturity:
          expiration > 0
            ? {
                kind: 'fixed-date',
                maturity: expiration,
                maturityIso: new Date(expiration * 1000).toISOString(),
                atMaturity: 'liquidatable',
              }
            : undefined,
        exit: {
          // Repaying is not free here: the reserve haircut means the net cost
          // is `faceDelta − assignedReserve`, and it is resolved at repay time.
          earlyRepay: 'discount',
          atMaturityCost: 'accrued',
          lateBehaviour: 'liquidatable',
          partialAllowed: true,
        },
        liquidation: {
          // A Dutch-auction challenge, not a repay-and-seize.
          model: 'auction',
          absorber: 'liquidator',
          // Price-triggered, but against the OWNER-DECLARED price — and with
          // no health gate whatsoever.
          trigger: 'price',
          seizure: 'proportional',
          permissioned: false,
          windowSecs: challengePeriod > 0 ? challengePeriod : undefined,
          penalty: challengerPct != null ? challengerPct / 100 : 0,
          penalties:
            challengerPct != null
              ? [
                  {
                    id: 'challenger-reward',
                    label: 'Challenger reward',
                    value: challengerPct / 100,
                    description:
                      'Paid to whoever successfully challenges the declared price.',
                  },
                ]
              : undefined,
          badDebt: 'socialized',
        },
        fees: fees as never,
        counterparty: { kind: 'cdp', solvency: 'overcollateralized' },
      },
      constraints: {
        crossMargin: false,
        // Each position is its own CONTRACT, cloned from an "original".
        positionModel: 'account',
        positionIdMeaning:
          'Each position is its own contract, cloned from the market’s original position; you own the clone.',
      },
    }
  },
}
